// **Das Formular eines Modells ↔ der Rumpf für `PUT /instance/models`** — rein,
// getestet (model-editor-map.test.ts). Die Regeln der Instanz gelten dort;
// hier nur, was die Vanilla-Fassung aus dem Formular las.

import type { InstanceModel, ModelPrice, ModelProfile } from "../../api/types";
import { pickOption, type SelectOptions } from "../../ui/select-menu";

export const EFFORTS: ReadonlyArray<readonly [string, string]> = [["low", "Niedrig"], ["medium", "Mittel"], ["high", "Hoch"], ["xhigh", "Sehr hoch"], ["max", "Maximal"]];
export const EFFORT_NAME: Readonly<Record<string, string>> = Object.fromEntries(EFFORTS);
/** Was die Oberfläche der Instanz anbietet, wenn ein Profil nichts sagt. */
export const DEFAULT_EFFORTS: readonly string[] = ["low", "medium", "high"];

/** Dieselben Schlüssel wie `PROVIDERS` in `client/src/modules/model-catalog.ts` der Instanz. */
export const PROVIDERS: SelectOptions = [
  ["", "aus dem Namen erkennen"], ["anthropic", "Anthropic"], ["openai", "OpenAI"], ["google", "Google"], ["meta", "Meta"],
  ["mistral", "Mistral AI"], ["deepseek", "DeepSeek"], ["xai", "xAI"], ["qwen", "Qwen"], ["cohere", "Cohere"],
  ["microsoft", "Microsoft"], ["amazon", "Amazon"], ["nvidia", "NVIDIA"], ["moonshot", "Moonshot AI"], ["zhipu", "Zhipu AI"],
  ["minimax", "MiniMax"], ["perplexity", "Perplexity"], ["ibm", "IBM"], ["ai21", "AI21 Labs"], ["yi", "01.AI"],
];

export const PRICE_FIELDS: ReadonlyArray<readonly [keyof ModelPrice, string]> = [["input", "Eingabe"], ["output", "Ausgabe"], ["cache_read", "Cache lesen"], ["cache_write", "Cache schreiben"]];

export const MODEL_ERRORS: Record<string, string> = {
  invalid_effort_not_offered: "Der Standard-Aufwand muss eine angebotene Stufe sein.",
  description_too_long: "Die Beschreibung ist zu lang.",
  invalid_price: "Ungültiger Preis.",
  invalid_usage: "Der Verbrauch liegt zwischen 0 und 100.",
};

export interface ModelEditorState {
  description: string;
  provider: string;
  usage: string;
  /** Angebotene Stufen (nur unterstützte zählen). */
  efforts: string[];
  /** Standard-Aufwand, leer = Vorgabe der Oberfläche. */
  effort: string;
  price: Record<keyof ModelPrice, string>;
}

/** Die Stufen, die an einem Endpoint dieses Mandanten bei dem Modell ankommen — in der Reihenfolge der Leiter. */
export const supportedEfforts = (m: InstanceModel): Array<readonly [string, string]> => EFFORTS.filter(([e]) => m.effort_supported.includes(e));

export function effortOptions(m: InstanceModel): SelectOptions {
  return [["", "Vorgabe der Oberfläche"], ...supportedEfforts(m)];
}

export function fromProfile(m: InstanceModel, p: ModelProfile, price: ModelPrice | undefined): ModelEditorState {
  return {
    description: p.description ?? "",
    provider: pickOption(p.provider ?? "", PROVIDERS),
    usage: p.usage === null || p.usage === undefined ? "" : String(p.usage),
    efforts: [...(p.efforts ?? DEFAULT_EFFORTS)],
    effort: pickOption(p.effort ?? "", effortOptions(m)),
    price: {
      input: String(price?.input ?? ""),
      output: String(price?.output ?? ""),
      cache_read: String(price?.cache_read ?? ""),
      cache_write: String(price?.cache_write ?? ""),
    },
  };
}

export interface ModelEditorBody {
  model: string;
  profile: { description: string; provider: string; usage: number | ""; effort?: string; efforts?: string[] };
  price: { input: number; output: number; cache_read: number; cache_write: number } | null;
}

/**
 * Aufwand nur, wenn das Modell überhaupt Stufen kennt (sonst gab es die
 * Felder nicht); ein Preis nur, wenn mindestens ein Feld gefüllt ist — leere
 * Felder daneben zählen als 0.
 */
export function toBody(model: string, s: ModelEditorState, m: InstanceModel): ModelEditorBody {
  const profile: ModelEditorBody["profile"] = {
    description: s.description,
    provider: s.provider,
    usage: s.usage.trim() === "" ? "" : Number(s.usage),
  };
  if (m.effort) {
    profile.effort = s.effort;
    profile.efforts = supportedEfforts(m).map(([e]) => e).filter((e) => s.efforts.includes(e));
  }
  const p = PRICE_FIELDS.map(([k]) => s.price[k].trim());
  const price = p.every((x) => x === "") ? null : {
    input: Number(p[0] || 0), output: Number(p[1] || 0), cache_read: Number(p[2] || 0), cache_write: Number(p[3] || 0),
  };
  return { model, profile, price };
}
